import React, { memo, useCallback, useContext, useEffect, useState } from 'react'
import { SeriesGroup } from '../../chart.typings'
import { alpha, Menu, MenuItem } from '@mui/material'
import { darkHoverBtnColor, lightHoverBtnColor } from '../../../../theme/theme'
import AddToWatchlistMenuItem from './menuItems/addToWatchlistMenuItem'
import { ChartContext } from '../../chart.context'
import SaveExpressionMenuItem from './menuItems/saveExpressionMenuItem'
import { UltraSmallButton } from '../../../custom'
import { selectMultiPaneFullScreen } from '../../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../../assets/constants'
import { useSelector } from 'react-redux'
import AlertContextMenuItem from '../../chartContextMenuOptions/AlertContextMenuItem'
import { selectIsEmbedded } from '../../../../selectors/device.ts'
import { isEmbeddedStrategyChartContext } from '../../../../util/embeddedChartContext'
import { callNative } from '../../../../util/nativeBridge'
import MoreHorizontalIcon from '../../../../assets/icons/MoreHorizontalIcon'

export type SeriesGroupMenuProps = {
    seriesGroup: SeriesGroup
    onClosed: Function
    onOpened: Function
    disabled?: boolean
}
const SeriesGroupMenu = (props: SeriesGroupMenuProps) => {
    const { seriesGroup, onClosed, onOpened, disabled = false } = props
    const chartContext = useContext(ChartContext)
    const expressionMode = chartContext?.chart.mode === 'EXPRESSION'
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const menuItems: any[] = []
    const isEmbedded = useSelector(selectIsEmbedded)

    const handleMoreClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (menuItems.length === 0) {
                return
            }
            if (isEmbeddedStrategyChartContext()) {
                callNative('onStrategyChartMenuClick')
                return
            }
            if (!isEmbedded) {
                setAnchorEl(e.currentTarget)
            } else {
                callNative('onSeriesMetadataMoreMenuClicked')
            }
        },
        [isEmbedded, menuItems.length, expressionMode, chartContext?.chart?.expression, seriesGroup]
    )

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    useEffect(() => {
        if (anchorEl) {
            onOpened()
        } else {
            onClosed()
        }
    }, [anchorEl])

    if (seriesGroup.primary) {
        menuItems.push(<AddToWatchlistMenuItem seriesGroup={seriesGroup} key={seriesGroup.id} onClosed={handleClose} />)
        menuItems.push(<AlertContextMenuItem viewKey={chartContext?.viewKey ?? ''} symbolName={'NIFTY'} handleCloseMenu={handleClose} />)
    }
    if (expressionMode && seriesGroup.primary && !isEmbeddedStrategyChartContext()) {
        menuItems.push(<SaveExpressionMenuItem seriesGroup={seriesGroup} onClosed={handleClose} />)
    }
    const container = useSelector(selectMultiPaneFullScreen)

    return (
        <>
            {(menuItems.length > 0) && <UltraSmallButton color={'inherit'} onClick={handleMoreClick} sx={{
                height: '100%',
                '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.mode === 'light' ? lightHoverBtnColor : darkHoverBtnColor, 0.8),
                },
            }}
                                                         data-event={'SeriesMetadata More Clicked'}
                                                         data-event-series-name={seriesGroup.name} disabled={disabled || !menuItems.length}>
                <MoreHorizontalIcon fontSize="small" color={'inherit'}/>
            </UltraSmallButton>}
            {Boolean(anchorEl) && !isEmbedded && (menuItems.length > 0) && <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && (menuItems.length > 0)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                onClose={handleClose}
                sx={{
                    padding: 0,
                    margin: 0
                }}
            >
                {
                    menuItems.map((mi) => {
                        return (
                            <MenuItem
                                key={mi.key}
                            >
                                {mi}
                            </MenuItem>
                        )
                    })
                }
            </Menu>}
        </>
    )
}

export default memo(SeriesGroupMenu)
